import React, {Component} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import {withRouter} from 'react-router-dom';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../constants';
import Util from '../../forecast-app/shared/util/util';
import InlineLoader from '../../forecast-app/shared/components/inline-loader/inline_loader';
import GenericModal from './generic_modal';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import ImportQuickbooksCustomersMutation from '../../mutations/ImportQuickbooksCustomersMutation';
import ImportQuickbooksCustomersReviewedMutation from '../../mutations/ImportQuickbooksCustomersReviewedMutation';
import {Checkbox} from '@/web-components';
import {hideLoader, showLoader} from '../global_loader';
import DirectApi from '../../directApi';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import MatchReviewStep from './integration_match_review/match_review_step';
import {carryOverSelection, importCount, toResolutions} from './integration_match_review/resolution_state';
import {
	QUICKBOOKS_MATCH_REVIEW,
	customerIndex,
	customersToImport,
	sourceIdOf,
	toNeutralGroups,
	toQuickbooksResolutions,
} from './quickbooks_match_review/quickbooksMatchReview';

const sortByName = customers =>
	[...customers].sort((a, b) => {
		if (a.name.toUpperCase().trim() > b.name.toUpperCase().trim()) {
			return 1;
		} else if (a.name.toUpperCase().trim() < b.name.toUpperCase().trim()) {
			return -1;
		}
		return 0;
	});

/** The correlation id graphql puts in a failed mutation's error ("Internal Error: <cid>", "404: <cid>"), or null. */
const correlationIdOf = error => {
	const found = /(?:Internal Error|404): ([\w-]+)/.exec(String(error));
	return found ? found[1] : null;
};

class QuickbooksImportCustomersModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			qboCustomers: [],
			fetchingCustomers: true,
			selectedCustomers: [],
			searchInput: '',
			selectAll: false,
			importing: false,
			// T4-144. 'select' is the step that has always existed; 'review' appears only when the preview found
			// a match that needs the Admin's decision, or customers it will link automatically.
			step: 'select',
			matchGroups: [],
			autoLinkCount: 0,
			createCount: 0,
			selection: {},
			previewing: false,
			// The customers the review step's preview was asked about: what its import sends, whatever the list's ticks
			// say by then (spec 015).
			previewedCustomers: [],
			// Once the review step has rendered, every import this modal sends is a reviewed one - so a retry after
			// a failed import can never fall back to creating every customer (spec 015).
			reviewShown: false,
			importError: null,
		};

		this.getContacts();
	}

	componentWillUnmount() {
		// A preview answering after the Admin closed the modal must not import (spec 015).
		this.closed = true;
		// Nor may its full-page loader outlive the modal until the preview answers; `proceed` then leaves the loader alone.
		if (this.state.previewing) hideLoader();
		// A reviewed import that did import, but told the Admin some confirmed pairings were not linked, kept the
		// modal open instead of reloading; closing it reloads now, so the clients list shows what was imported. Not
		// while an import is still running: its own outcome then reloads, or the default handler reports it.
		if (this.reloadOnClose && !this.committing) window.location.reload();
	}

	getContacts() {
		DirectApi.Fetch('quickbooks/unimported_customers').then(contacts => {
			this.setState({
				qboCustomers: sortByName(contacts),
				fetchingCustomers: false,
			});
		});
	}

	toggleSelectAll() {
		if (this.state.selectAll) {
			this.setState({
				selectedCustomers: [],
				selectAll: false,
			});
		} else {
			this.setState({
				selectedCustomers: this.state.qboCustomers.map(qboContact => ({
					id: qboContact.id,
					realmId: qboContact.realmId,
				})),
				selectAll: true,
			});
		}
	}

	toggleContact(customer) {
		const key = sourceIdOf(customer);
		const alreadySelected = this.state.selectedCustomers.some(c => sourceIdOf(c) === key);

		if (alreadySelected) {
			this.setState({
				selectedCustomers: this.state.selectedCustomers.filter(c => sourceIdOf(c) !== key),
				selectAll: false,
			});
		} else {
			this.setState(prevState => ({
				selectedCustomers: [...prevState.selectedCustomers, {id: customer.id, realmId: customer.realmId}],
			}));
		}
	}

	/**
	 * CREATE: today's import for a company without the flag; otherwise the preview first (T4-144). Once the review step
	 * has been shown, always the preview: a flag cache turning off mid-session must not send an unreviewed import.
	 */
	createClicked() {
		if (!this.state.reviewShown && !hasFeatureFlag('qbo_client_contact_sync')) {
			this.syncContacts();
			return;
		}
		this.reviewMatches();
	}

	/**
	 * Today's import, exactly: used with the flag off, and with the flag on before any review step was shown - then
	 * with the customers that were previewed.
	 */
	syncContacts(qboCustomers = this.state.selectedCustomers) {
		// One activation, one import: `importing` reaches the button only on the next render.
		if (this.committing) return;
		this.committing = true;
		this.setState({importing: true});
		showLoader();
		Util.CommitMutation(
			ImportQuickbooksCustomersMutation,
			{
				qboCustomers,
			},
			() => {
				this.committing = false;
				this.setState({importing: false});
				this.props.closeModal();
				hideLoader();
				window.location.reload();
			}
		);
	}

	/**
	 * T4-144. Ask which selected customers need the Admin's decision before importing anything. Read-only. A preview
	 * that fails - no answer, any non-2xx (the Lambda's 503 when a read failed), or no `groups` list - imports nothing:
	 * it cannot tell "nothing to review" from "could not check", so the Admin is told and tries again (spec 015).
	 */
	reviewMatches() {
		// One click, one preview: the disabled button is the guard; this backs it up. The instance flags are set
		// synchronously - `previewing` and `importing` reach the state only on the next render.
		if (this.previewInFlight || this.committing || this.state.previewing || this.state.importing) return;
		this.previewInFlight = true;

		this.setState({previewing: true, importError: null});
		showLoader();

		// What is previewed is what is imported: a tick changed while the preview runs changes neither.
		const previewed = this.state.selectedCustomers;
		const init = {
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			credentials: 'include',
			body: JSON.stringify(previewed),
		};

		const proceed = preview => {
			this.previewInFlight = false;
			// A closed modal hid its loader at unmount; by now the loader may be a reopened modal's, so leave it.
			if (this.closed) {
				return;
			}
			hideLoader();

			if (!preview || !Array.isArray(preview.groups)) {
				// Fail closed: the ticks and `reviewShown` stay as they were, and CREATE is the retry.
				this.setState({
					previewing: false,
					importError: {kind: 'previewFailed', correlationId: null, refreshFailed: false},
				});
				return;
			}

			this.setState({previewing: false});
			const groups = preview.groups;
			const autoLinkCount = preview.autoLinkCount || 0;

			// Nothing to review and nothing to link automatically: the import goes ahead at once.
			if (groups.length === 0 && !(autoLinkCount > 0)) {
				if (this.state.reviewShown) {
					this.importReviewed(previewed, []);
				} else {
					this.syncContacts(previewed);
				}
				return;
			}

			// The review step: the groups to confirm or, with none, how many will be linked automatically - shown
			// before the import, never after it (review round 3, Amar's gap 2, AC8). Either way the step has been
			// shown, so its import, and every later one, is a reviewed one.
			this.setState(state => ({
				step: 'review',
				reviewShown: true,
				matchGroups: groups,
				previewedCustomers: previewed,
				autoLinkCount,
				createCount: preview.createCount || 0,
				// BACK, then CREATE: the picks that still fit the new groups are kept (review round 3, Amar's gap 3).
				selection: carryOverSelection(
					state.selection,
					this.neutralGroups(state.matchGroups),
					this.neutralGroups(groups)
				),
			}));
		};

		// The catch covers only the request and its body, so `proceed` - which may import - runs exactly once.
		return DirectApi.Fetch_Raw_With_Init('quickbooks/importMatchPreview', init)
			.then(result => (result && result.ok ? result.json() : null))
			.catch(() => null)
			.then(proceed);
	}

	selectResolution(groupKey, choice) {
		this.setState(state => ({selection: {...state.selection, [groupKey]: choice}}));
	}

	backToSelection() {
		// The ticks survive, and so do the choices: the next CREATE previews again and keeps each one that still fits
		// its group (carryOverSelection). The search is cleared, so no filter the Admin can no longer see hides a
		// customer.
		this.setState({step: 'select', searchInput: ''});
	}

	neutralGroups(groups = this.state.matchGroups) {
		const {formatMessage} = this.props.intl;
		return toNeutralGroups(groups, {
			clientName: clientId => formatMessage({id: 'integrations.quickbooks.match_review.unnamed_client'}, {id: clientId}),
			companyName: () => formatMessage({id: 'integrations.quickbooks.match_review.unnamed_company'}),
		});
	}

	importFromReview() {
		const pairings = toResolutions(this.neutralGroups(), this.state.selection);
		const resolutions = toQuickbooksResolutions(pairings, customerIndex(this.state.matchGroups));
		this.importReviewed(customersToImport(this.state.previewedCustomers, this.state.matchGroups, resolutions), resolutions);
	}

	/**
	 * A reviewed import: only what is to be imported, with the Admin's pairings - `resolutions` always given, so the
	 * Lambda fails closed rather than creating everything. Not re-sent by the browser (retry false), and a failure
	 * keeps the modal (no page reload) so a retry is reviewed again.
	 */
	importReviewed(qboCustomers, resolutions) {
		// One activation, one import: `importing` reaches the button only on the next render, and this import is
		// neither idempotent nor re-sent.
		if (this.committing) return;
		this.committing = true;
		this.setState({importing: true, importError: null});
		showLoader();
		Util.CommitMutation(
			ImportQuickbooksCustomersReviewedMutation,
			{qboCustomers, resolutions},
			response => this.reviewedImportSucceeded(response),
			false,
			null,
			(transaction, onFailure) => this.reviewedImportFailed(transaction, onFailure)
		);
	}

	/**
	 * A confirmed pairing the Lambda could not link (the client was taken meanwhile, the link failed...) leaves its
	 * customer neither linked nor created. Then the modal stays, says so, and re-reads the list - the customer is still
	 * in it - instead of reloading over the message; closing it reloads. Otherwise, today's close and reload.
	 */
	reviewedImportSucceeded(response) {
		this.committing = false;
		const unlinked =
			(response && response.importQuickbooksCustomers && response.importQuickbooksCustomers.unlinkedConfirmedCount) || 0;
		if (unlinked > 0 && !this.closed) {
			hideLoader();
			this.reloadOnClose = true;
			this.setState({
				importing: false,
				step: 'select',
				fetchingCustomers: true,
				searchInput: '',
				importError: {kind: 'unlinkedConfirmed', count: unlinked, correlationId: null, refreshFailed: false},
			});
			this.refreshCustomers();
			return;
		}
		// Closed while it ran (Escape): nothing here can show the message, so today's reload.
		if (!this.closed) this.setState({importing: false});
		// This reload stands for the one an earlier message deferred to the close.
		this.reloadOnClose = false;
		this.props.closeModal();
		hideLoader();
		window.location.reload();
	}

	reviewedImportFailed(transaction, onFailure) {
		this.committing = false;
		hideLoader();
		if (this.closed) {
			// Closed (Escape) while the import ran: nothing here can show the error, and a fresh modal starts
			// unreviewed whether or not the page reloads, so the default handler reports it.
			onFailure(transaction);
			return;
		}
		if (String(transaction).includes('No Viewer:')) {
			// A lost session: the default handler sends the Admin to log in.
			this.setState({importing: false});
			onFailure(transaction);
			return;
		}
		// The list is re-read before anything can be sent again: CREATE stays disabled while it loads, so the next
		// import is picked from the re-read list. Not a guarantee: after a transport failure ("Failed to fetch") the
		// import may still be running when the list is read, so a customer it creates after the read can stay listed
		// and ticked and be sent again - the default handler's reload had the same race.
		this.setState({
			importing: false,
			step: 'select',
			fetchingCustomers: true,
			// The refreshed list is never silently filtered.
			searchInput: '',
			importError: {kind: 'importFailed', correlationId: correlationIdOf(transaction), refreshFailed: false},
		});
		this.refreshCustomers();
	}

	/**
	 * The refresh-failed banner's Try again: the same re-read as after the failure, in this modal, so it stays a
	 * reviewed one. Closing it instead would start the next modal unreviewed.
	 */
	retryRefresh() {
		this.setState(state => ({fetchingCustomers: true, importError: {...state.importError, refreshFailed: false}}));
		this.refreshCustomers();
	}

	/** Stands in for the reload the default error handler would do: customers imported meanwhile drop off. */
	refreshCustomers() {
		DirectApi.Fetch_Raw('quickbooks/unimported_customers')
			.then(result => (result && result.ok ? result.json() : Promise.reject(new Error('refresh failed'))))
			.then(contacts => {
				if (this.closed) return;
				const sorted = sortByName(contacts);
				const listed = new Set(sorted.map(sourceIdOf));
				this.setState(state => ({
					qboCustomers: sorted,
					fetchingCustomers: false,
					selectedCustomers: state.selectedCustomers.filter(c => listed.has(sourceIdOf(c))),
					selectAll: false,
				}));
			})
			.catch(() => {
				if (this.closed) return;
				this.setState(state => ({
					qboCustomers: [],
					fetchingCustomers: false,
					selectedCustomers: [],
					selectAll: false,
					importError: {...state.importError, refreshFailed: true},
				}));
			});
	}

	/** The banner's message and data-cy: a failed preview, confirmed pairings not linked, or a failed import. */
	importErrorMessage(importError) {
		if (importError.kind === 'previewFailed') {
			return {
				cy: 'quickbooks-preview-error',
				message: <FormattedMessage id="integrations.quickbooks.match_review.preview_failed" />,
			};
		}
		if (importError.kind === 'unlinkedConfirmed') {
			// The import did happen, so "could not be completed" would be untrue even if the re-read fails; the
			// re-read's own failure shows as Try again.
			return {
				cy: 'quickbooks-import-unlinked',
				message: (
					<FormattedMessage
						id="integrations.quickbooks.match_review.confirmed_not_linked"
						values={{count: importError.count}}
					/>
				),
			};
		}
		return {
			cy: 'quickbooks-import-error',
			message: (
				<FormattedMessage
					id={
						importError.refreshFailed
							? 'integrations.quickbooks.match_review.refresh_failed'
							: 'integrations.quickbooks.match_review.import_failed'
					}
				/>
			),
		};
	}

	renderImportError() {
		const {importError} = this.state;
		if (!importError) return null;
		const {cy, message} = this.importErrorMessage(importError);
		// Wrapped in .integration-match-review, the only scope that styles .integration-match-banner. An alert: focus
		// moves to the remounted list, so it would otherwise go unannounced.
		return (
			<div className="integration-match-review">
				<div className="integration-match-banner integration-match-banner--error" role="alert" data-cy={cy}>
					<span>{message}</span>
					{importError.correlationId ? (
						<>
							{' '}
							<span>
								<FormattedMessage
									id="integrations.quickbooks.match_review.error_reference"
									values={{correlationId: importError.correlationId}}
								/>
							</span>
						</>
					) : null}
					{importError.refreshFailed ? (
						<button
							type="button"
							className="integration-match-banner-action"
							onClick={() => this.retryRefresh()}
							data-cy="quickbooks-import-try-again"
						>
							<FormattedMessage id="integrations.quickbooks.match_review.try_again" />
						</button>
					) : null}
				</div>
			</div>
		);
	}

	render() {
		const {formatMessage} = this.props.intl;
		const reviewing = this.state.step === 'review';
		const neutralGroups = reviewing ? this.neutralGroups() : [];
		const willImport = importCount(neutralGroups, this.state.selection, this.state.autoLinkCount, this.state.createCount);
		const refreshFailed = !!(this.state.importError && this.state.importError.refreshFailed);

		const selectContent = (
			<div className="integration-sync-clients-modal-content">
				{this.renderImportError()}
				<div className="integration-sync-clients-modal-description">
					<FormattedMessage id="integrations.quickbooks.sync_clients.description" />
				</div>
				{/* After a failed re-read there is no list to show: "all customers synchronised" would be untrue. */}
				{this.state.fetchingCustomers ? (
					<InlineLoader />
				) : refreshFailed ? null : (
					<>
						<input
							autoFocus={true}
							value={this.state.searchInput}
							onChange={event => {
								this.setState({searchInput: event.target.value});
							}}
						/>

						<CustomScrollDiv autoHeight autoHeightMin={10} autoHeightMax={'60vh'}>
							<div className="integration-clients">
								{this.state.qboCustomers && this.state.qboCustomers.length > 0 ? (
									<>
										<div className="integration-client" key={'0'}>
											<Checkbox
												checked={this.state.selectAll}
												onClick={this.toggleSelectAll.bind(this)}
											/>
											<FormattedMessage id="common.select_all" />
										</div>
										{this.state.qboCustomers
											.filter(x => x.name.toLowerCase().includes(this.state.searchInput.toLowerCase()))
											.map(qboCustomer => (
												<div className="integration-client" key={sourceIdOf(qboCustomer)}>
													<Checkbox
														checked={this.state.selectedCustomers.some(
															c => sourceIdOf(c) === sourceIdOf(qboCustomer)
														)}
														onClick={this.toggleContact.bind(this, qboCustomer)}
														cy={`quickbooks-import-customer-${sourceIdOf(qboCustomer)}`}
													/>
													{qboCustomer.name}
												</div>
											))}
									</>
								) : (
									<FormattedMessage id="integrations.quickbooks.all_contacts_synched" />
								)}
							</div>
						</CustomScrollDiv>
					</>
				)}
			</div>
		);

		const selectButtons = [
			{
				text: formatMessage({id: 'common.cancel'}),
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.WHITE,
			},
			{
				text: formatMessage({id: 'common.create_x_clients'}, {numOfClients: this.state.selectedCustomers.length}),
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.GREEN,
				callback: this.createClicked.bind(this),
				disabled:
					this.state.importing ||
					this.state.previewing ||
					this.state.fetchingCustomers ||
					refreshFailed ||
					this.state.selectedCustomers.length === 0,
				defaultCallback: () => null,
				preventDefaultClose: true,
				cy: 'quickbooks-import-create',
			},
		];

		const reviewButtons = [
			{
				text: formatMessage({id: 'common.back'}),
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.WHITE,
				callback: this.backToSelection.bind(this),
				defaultCallback: () => null,
				preventDefaultClose: true,
				cy: 'quickbooks-import-back',
			},
			{
				text: formatMessage({id: 'integrations.quickbooks.match_review.import_customers'}, {count: willImport}),
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.GREEN,
				// An arrow keeps `this` and forwards nothing. GenericModal calls it with no arguments, so a bind would also do.
				callback: () => this.importFromReview(),
				disabled: this.state.importing || willImport === 0,
				defaultCallback: () => null,
				preventDefaultClose: true,
				cy: 'quickbooks-import-confirm',
			},
		];

		return (
			<GenericModal
				headerText={
					<FormattedMessage
						id={reviewing ? 'integrations.quickbooks.match_review.title' : 'integrations.quickbooks.sync_clients'}
					/>
				}
				closeModal={this.props.closeModal}
				// Once the review step has been shown, a stray click outside would throw away its picks and `reviewShown`,
				// starting the next modal unreviewed - on either step. Before that, a click outside closes it as always.
				preventClickAwayClose={this.state.reviewShown}
				className="integration-sync-clients-modal"
				buttons={reviewing ? reviewButtons : selectButtons}
				content={
					reviewing ? (
						<MatchReviewStep
							groups={neutralGroups}
							autoLinkCount={this.state.autoLinkCount}
							selectedCount={this.state.previewedCustomers.length}
							selection={this.state.selection}
							onSelect={this.selectResolution.bind(this)}
							provider={QUICKBOOKS_MATCH_REVIEW}
						/>
					) : (
						selectContent
					)
				}
			/>
		);
	}
}

export default injectIntl(withRouter(QuickbooksImportCustomersModal));
